"use client";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { Button } from "../ui/button";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import { Input } from "../ui/input";
import { useLocale, useTranslations } from "next-intl";
import { addToCart } from "@/services/cart.service";
import toast from "react-hot-toast";
import { useToken } from "@/lib/use-token";
import { useRouter } from "next/navigation";
import { VariantModel } from "@/types/product";
import { cn } from "@/lib/utils";
import { COLOR_VARIANT_ID } from "@/lib/constants";
import { Separator } from "../ui/separator";
import { flyToCart } from "@/lib/fly-to-cart";

const AddToCart = ({
  productId,
  variants = [],
  price = 0,
  isDetails = false,
  slug,
}: {
  productId: number;
  slug?: string;
  variants?: VariantModel[];
  price?: number;
  isDetails?: boolean;
}) => {
  const t = useTranslations();
  const { hasToken } = useToken();
  const router = useRouter();
  const [qty, setQty] = useState(1);
  const [isPending, startTransition] = useTransition();
  const locale = useLocale();

  // Track selected variant values by variant ID
  const [selectedVariants, setSelectedVariants] = useState<
    Record<number, { id: number; value: string; extra_price: number }>
  >({});

  const increment = () => setQty((p) => p + 1);
  const decrement = () => setQty((p) => (p > 1 ? p - 1 : 1));

  const handleVariantSelect = (
    variantId: number,
    value: { id: number; value: string; extra_price: number },
  ) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [variantId]: value,
    }));
    if (variantId === COLOR_VARIANT_ID && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(`product-color-change:${productId}`, {
          detail: value.value,
        }),
      );
    }
  };

  // 💰 Calculate total extra price
  const extraPrice = useMemo(() => {
    return Object.values(selectedVariants).reduce(
      (sum, v) => sum + (v.extra_price || 0),
      0,
    );
  }, [selectedVariants]);

  const finalPrice = useMemo(() => price + extraPrice, [price, extraPrice]);

  const handleAddToCart = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (!isDetails) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!hasToken()) {
      toast(t("youShouldLoginFirst"));
      router.push("/login");
      return;
    }
    const hasVariants = variants.length > 0;
    if (!isDetails && hasVariants) {
      toast("الرجاء اختيار جميع المتغيرات");
      router.push(`/${locale}/products/${slug}`);
      return;
    }
    // Ensure all variants are selected
    if (
      variants.length > 0 &&
      Object.keys(selectedVariants).length < variants.length
    ) {
      toast.error("الرجاء اختيار جميع المتغيرات أولاً");
      return;
    }

    // Prepare selected variant IDs for backend
    const variantValueIds = Object.values(selectedVariants).map((v) => v.id);
    startTransition(async () => {
      try {
        const response = await addToCart({
          product_id: productId,
          quantity: qty,
          attributes: variantValueIds,
        });

        if (response.status === 200) {
          // toast.success(t("addedToCartSuccessfully"));
          flyToCart(`product-img-${productId}`);
        }
      } catch (err) {
        console.error(err);
        toast.error("حدث خطأ أثناء الإضافة إلى السلة");
      }
    });
  };

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 },
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, []);

  return (
    <>
      {isDetails && <div ref={sentinelRef} />}
      <div
        className={cn(
          "space-y-6 bg-white py-3",
          isDetails && "sticky bottom-0 sm:static",
          // isSticky && " px-2 -mx-4",
        )}
      >
        {isDetails && variants.length > 0 && (
          <div className="space-y-5">
            {variants.map((variant) => (
              <div key={variant.id} className="space-y-2">
                <h2 className="font-medium">{variant.name}</h2>
                <div className="flex flex-wrap gap-3">
                  {variant.values.map((value) => {
                    const isSelected =
                      selectedVariants[variant.id]?.id === value.id;
                    return (
                      <span
                        key={value.id}
                        onClick={() => handleVariantSelect(variant.id, value)}
                        className={cn(
                          "cursor-pointer border rounded-lg px-3 py-1 transition duration-300 ease-in-out bg-primary/5",
                          isSelected
                            ? " border-2 border-primary bg-primary/10"
                            : "border-gray-300 hover:border-primary hover:bg-primary/10",
                        )}
                      >
                        {variant.id === COLOR_VARIANT_ID ? (
                          <span
                            className={cn(
                              "inline-block w-5 h-5 rounded-full  -mb-[5px]",
                              locale === "ar" ? "ml-2" : "mr-2",
                            )}
                            style={{
                              backgroundColor: value.value,
                            }}
                          />
                        ) : (
                          <span>{value.value}</span>
                        )}
                        {value.extra_price > 0 && (
                          <span className={cn("ml-1 text-xs")}>
                            (+{value.extra_price.toLocaleString("en-US")} SYP)
                          </span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* 💰 Display final price */}
            <div className="text-lg font-semibold mt-4">
              <span>
                {t("finalPrice")}:{" "}
                <span dir="ltr">{finalPrice.toLocaleString()} SYP </span>
              </span>
              {qty > 1 && (
                <span className="text-sm text-muted-foreground ">
                  × {qty} = {(finalPrice * qty).toLocaleString("en-US")} SYP
                </span>
              )}
            </div>
            <Separator />
          </div>
        )}

        <div className=" flex items-center gap-3">
          {isDetails && (
            <div className="w-full sm:w-auto flex items-center justify-between border rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={decrement}
                className="rounded-none bg-accent border"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 1)}
                className="w-18 px-0 text-center border-0 focus-visible:ring-0"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={increment}
                className="rounded-none bg-accent border"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
          <Button
            loading={isPending}
            className={
              isDetails ? "w-1/2 sm:w-1/3" : "!w-8 !h-8 sm:!w-full sm:!h-full"
            }
            onClick={handleAddToCart}
          >
            <ShoppingCart className="h-5 w-5" />
            {isDetails && (
              <span className="hidden sm:block">{t("addToCart")}</span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddToCart;
